import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { formatBoardRevision, formatDeviceUidHex } from '../utils/deviceFormatters';
import type { RootState } from './Store';

// This reducer is to track information about connected controller
const initialState = {
  sku: '',
  boardRevision: null as number | null,
  firmwareBootloader: '',
  mainAppVersion: '',
  uid: '' as string,
  connectionType: null as number | null,
  batteryLevel: null as string | null,
  batteryPercentage: null as number | null,
  batteryCharging: false,
  updateInProgress: false,
  updateProgress: 0,
};

export const controllerDataSlice = createSlice({
  name: 'controllerData',
  initialState,
  reducers: {
    onClearControllerInfo: (state) => ({
      ...state,
      sku: initialState.sku,
      boardRevision: initialState.boardRevision,
      firmwareBootloader: initialState.firmwareBootloader,
      mainAppVersion: initialState.mainAppVersion,
      uid: initialState.uid,
      connectionType: initialState.connectionType,
      batteryLevel: initialState.batteryLevel,
      batteryPercentage: initialState.batteryPercentage,
      batteryCharging: initialState.batteryCharging,
      updateInProgress: initialState.updateInProgress,
      updateProgress: initialState.updateProgress,
    }),
    onUpdateControllerInfo: (state, { payload }) => {
      // We only update the parts we get in the payload.  That way we don't overwrite parts
      // that are not present in the payload.  In case we want to update one part at a time.
      // When a connection changes we clear

      // This is the SKU for the controller.
      if (payload.devicePartNumber !== undefined) {
        state.sku = payload.devicePartNumber;
      }

      // This is the main app version for the controller.
      if (payload.firmwareMainApp != null) {
        state.mainAppVersion = payload.firmwareMainApp;
      } else if (payload.maMajor !== undefined && payload.maMinor !== undefined && payload.maRevision !== undefined) {
        const mainAppVersion = payload.maMajor.toString().concat('.', payload.maMinor, '.', payload.maRevision);
        state.mainAppVersion = mainAppVersion;
      }

      // This is the firmware bootloader version for the controller.
      if (payload.firmwareBootloader != null) {
        state.firmwareBootloader = payload.firmwareBootloader;
      } else if (payload.blMajor !== undefined && payload.blMinor !== undefined && payload.blRevision !== undefined) {
        const firmwareBootloaderVersion = payload.blMajor.toString().concat('.', payload.blMinor, '.', payload.blRevision);
        state.firmwareBootloader = firmwareBootloaderVersion;
      }

      // This is the UID for the controller.
      if (payload.uid !== undefined && payload.uidSize !== undefined) {
        state.uid = formatDeviceUidHex(payload.uid, payload.uidSize) ?? '';
      }

      // This is the board revision for the controller.
      if (payload.boardRevision !== undefined) {
        state.boardRevision = formatBoardRevision(payload.boardRevision);
      }

      // This is the update in progress for the controller.
      if (payload.updateInProgress !== undefined) {
        state.updateInProgress = payload.updateInProgress;
      }

      // This is the update progress for the controller.
      if (payload.updateProgress !== undefined) {
        state.updateProgress = payload.updateProgress;
      }
    },
    onUpdateControllerConnectionType: (state, { payload }) => {
      state.connectionType = payload;
    },
    onUpdateControllerBatteryLevel: (
      state,
      {
        payload,
      }: PayloadAction<{
        controllerCharging: boolean;
        controllerBatteryLevel: string | null;
        controllerBatteryPercentage?: number | null;
      }>
    ) => {
      state.batteryCharging = payload.controllerCharging;
      state.batteryLevel = payload.controllerBatteryLevel;
      state.batteryPercentage = payload.controllerBatteryPercentage ?? null;
    },
    onClearControllerConnectionState: (state) => {
      state.connectionType = initialState.connectionType;
      state.batteryLevel = initialState.batteryLevel;
      state.batteryPercentage = initialState.batteryPercentage;
      state.batteryCharging = initialState.batteryCharging;
      state.sku = initialState.sku;
      state.boardRevision = initialState.boardRevision;
      state.firmwareBootloader = initialState.firmwareBootloader;
      state.mainAppVersion = initialState.mainAppVersion;
      state.uid = initialState.uid;
      state.batteryLevel = initialState.batteryLevel;
      state.batteryPercentage = initialState.batteryPercentage;
      state.batteryCharging = initialState.batteryCharging;
      state.updateInProgress = initialState.updateInProgress;
      state.updateProgress = initialState.updateProgress;
    },
  },
});

export const selectControllerData = (state: RootState) => state.controllerData;

export default controllerDataSlice.reducer;
